import styles from "./winners.module.css";
import { Winner, WinnersFilterProps } from "./types";
import { useContext, useRef, useState } from "react";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import SelectDropdown from "../../components/Dropdown/dropdown";
import { useAppDispatch } from "../../redux/hooks";
import {
  publishedFilteredPrizeCollectionWinners,
  publishFilteredWinners,
} from "../../redux/features/winners/winnersSlice";
import { PrizeCollectionWinner } from "../../collections/prize-collections/types";
import { CurrentPageContext } from "./winners-context";

const WinnersFilters = ({
  filters: { yearsArchive, years },
  winners,
  categories,
  prizeCollectionWinners,
}: WinnersFilterProps) => {
  const allYears = years ? ["All", ...years] : [];
  const dispatch = useAppDispatch();
  const [year, setYear] = useState<string | number>("All");
  const [categoryFilter, setCategoryFilter] = useState("Top 5s");
  const fireClearSearchCallback = useRef(true);
  const isWinnersPage = useContext(CurrentPageContext);

  let autoCompleteItems: any = [];
  if (isWinnersPage) {
    const prizeCategories = winners ? [...new Set(winners.map(w => w.Category))] : [];
    const makes = winners ? [...new Set(winners.map(w => w.Make))] : [];
    const winnerNames = winners ? [...new Set(winners.map(w => w.Name))] : [];
    const winnerStyles = winners ? [...new Set(winners.map(w => w.Styles).flat())] : [];
    autoCompleteItems = [...prizeCategories, ...makes, ...winnerNames, ...winnerStyles];
  } else {
    const winnerNames = prizeCollectionWinners ? [...new Set(prizeCollectionWinners.map(w => w.WinnerName))] : [];
    const prizesWon = prizeCollectionWinners ? [...new Set(prizeCollectionWinners.map(w => w.CarWon))] : [];
    autoCompleteItems = [...winnerNames, ...prizesWon];
  }

  const getFilteredWinnersForWinnersPage = (
    selectedWinner: string,
    selectedCategory: string,
    selectedYear: number | string
  ) => {
    if (selectedWinner === "" && selectedCategory === "Top 5s" && selectedYear === "All") {
      return winners;
    }
    return winners.filter(winner => {
      const matchesSearch =
        selectedWinner === "" ||
        winner.Name === selectedWinner ||
        winner.Category === selectedWinner ||
        winner.Make === selectedWinner ||
        winner.Styles?.includes(selectedWinner);
      const matchesCategory = selectedCategory === "Top 5s" || winner.WinnerCategories?.includes(selectedCategory);
      const matchesYear =
        selectedYear === "All" ||
        parseInt(winner.Date.Year, 10) === selectedYear ||
        (selectedYear === "Archive" && parseInt(winner.Date.Year, 10) <= yearsArchive[0]);
      return matchesSearch && matchesCategory && matchesYear;
    });
  };

  const getFilteredWinnersForPrizeCollectionPage = (selectedWinner: string, selectedYear: number | string) => {
    if (selectedWinner === "" && selectedYear === "All") {
      return prizeCollectionWinners;
    }
    return prizeCollectionWinners.filter(winner => {
      const matchesSearch =
        selectedWinner === "" || winner.WinnerName === selectedWinner || winner.CarWon === selectedWinner;
      const matchesYear = selectedYear === "All" || parseInt(winner.PickupDate.Year) === selectedYear;
      return matchesSearch && matchesYear;
    });
  };

  const getFilteredWinners = (
    selectedWinner: string,
    selectedCategory: string,
    selectedYear: number | string
  ): Winner[] | PrizeCollectionWinner[] => {
    if (isWinnersPage) {
      return getFilteredWinnersForWinnersPage(selectedWinner, selectedCategory, selectedYear);
    }
    return getFilteredWinnersForPrizeCollectionPage(selectedWinner, selectedYear);
  };

  const onYearFilter = (selectedYear: number | string) => {
    setYear(selectedYear);
    setCategoryFilter("Top 5s");
    clearWinnerSearch();
    const newWinnerList = getFilteredWinners("", "Top 5s", selectedYear);
    isWinnersPage
      ? dispatch(publishFilteredWinners({ winners: newWinnerList }))
      : dispatch(publishedFilteredPrizeCollectionWinners({ winners: newWinnerList }));
  };

  const onWinnerSelect = (selectedWinner: string) => {
    setYear("All");
    setCategoryFilter("Top 5s");
    const newWinnerList = getFilteredWinners(selectedWinner, "Top 5s", "All");
    isWinnersPage
      ? dispatch(publishFilteredWinners({ winners: newWinnerList }))
      : dispatch(publishedFilteredPrizeCollectionWinners({ winners: newWinnerList }));
  };

  const onFilterCategory = (selectedCategory: string) => {
    setCategoryFilter(selectedCategory);
    setYear("All");
    clearWinnerSearch();
    const newWinnerList = getFilteredWinners("", selectedCategory, "All");
    dispatch(publishFilteredWinners({ winners: newWinnerList }));
  };

  const onClearWinnerSearch = () => {
    if (fireClearSearchCallback.current) {
      onWinnerSelect("");
    }
  };

  const clearWinnerSearch = () => {
    const searchWinnerClearButton = document.querySelector(".clear-icon") as HTMLDivElement;
    if (searchWinnerClearButton) {
      fireClearSearchCallback.current = false;
      searchWinnerClearButton.click();
      fireClearSearchCallback.current = true;
    }
  };

  return (
    <>
      <article className={`${styles.winnersYearsFilter} overflow-auto whitespace-nowrap`}>
        {allYears.map(winnerYear => (
          <span
            key={winnerYear}
            data-testid={`year-${winnerYear}`}
            onClick={() => onYearFilter(winnerYear)}
            className={`${styles.yearsFilterLink} ${year === winnerYear ? styles.active : ""}`}>
            {winnerYear}
          </span>
        ))}
        {yearsArchive.length > 1 && (
          <span
            data-testid={`year-${yearsArchive[0]}-00`}
            onClick={() => onYearFilter("Archive")}
            className={`${styles.yearsFilterLink} ${year === "Archive" ? styles.active : ""}`}>
            {yearsArchive[0]} - {yearsArchive[yearsArchive.length - 1].toString().slice(-2)}
          </span>
        )}
        {yearsArchive.length == 1 && (
          <span
            data-testid="year-archive"
            onClick={() => onYearFilter("Archive")}
            className={`${styles.yearsFilterLink} ${year === "Archive" ? styles.active : ""}`}>
            {yearsArchive[0]}
          </span>
        )}
      </article>
      <article className={styles.categoriesAndSearchFilter}>
        <div className={`lg:px-60 xs:px-4 ${isWinnersPage ? "grid grid-cols-2 gap-4" : "grid grid-cols-1"}`}>
          {isWinnersPage && (
            <SelectDropdown
              defaultOptionValue="Top 5s"
              defaultOptionText="Top 5s"
              dropdownOptions={categories}
              onChange={onFilterCategory}
              selectedValue={categoryFilter}
              optionsParentDivClassName={`w-full left-0 ${styles.winnerCategories}`}
              dropdownParentDivClassName="w-auto border border-solid border-[#dfe1e5]"
            />
          )}
          <div data-testid="winner-autocomplete" className="mr-3">
            <ReactSearchAutocomplete
              styling={{ borderRadius: "5px", boxShadow: "none", zIndex: 1 }}
              items={autoCompleteItems?.map((item: string, idx: number) => ({
                id: idx,
                name: item,
              }))}
              resultStringKeyName="name"
              placeholder={"Search"}
              className={"reactSearchAutoComplete"}
              onSelect={(item: { name: string }) => onWinnerSelect(item.name)}
              onClear={onClearWinnerSearch}
              maxResults={autoCompleteItems?.length}
            />
          </div>
        </div>
      </article>
    </>
  );
};

export default WinnersFilters;
